import React, { useEffect, useRef, useState } from 'react';
import type { InputProps } from 'antd';
import { StyledNumericInput } from './index.styled';
import { stripFormatting, formatThousands } from './format';
import { parseFormattedNumber } from '@nxsys/utils/parseFormattedNumber';

// A "valid number in progress" — allows transient input the strict parser would
// reject (a lone "-", a trailing "10.", an unfinished "1e"). Anything outside
// this (letters, a second decimal point, "1.2.3") is malformed and surfaced as
// an inline error. £/$/€, commas and spaces are stripped before testing.
const PARTIAL_NUMBER = /^-?\d*\.?\d*(e[+-]?\d*)?$/i;

const invalidMessage = (raw: string): string => {
  const trimmed = raw.trim();
  const shown = trimmed.length > 30 ? `${trimmed.slice(0, 30)}…` : trimmed;
  return `"${shown}" is not a valid number`;
};

const toDisplay = (
  value: string | number | null | undefined,
  thousandSeparator: boolean,
): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const str = String(value);
  return thousandSeparator ? formatThousands(str) : str;
};

export type NumericInputProps = Omit<
  InputProps,
  'onChange' | 'value' | 'type'
> & {
  value?: string | number | null;
  /** Emits the cleaned numeric string ("1500"), or null when empty/invalid. */
  onChange?: (value: string | null) => void;
  /** Format with thousand separators on blur (default: true; off for counts). */
  thousandSeparator?: boolean;
};

/**
 * NX-3941: shared numeric text field. Unlike a native `<input type="number">`
 * (which silently coerces "12.34.56" → 12.3456) or antd `InputNumber` (which
 * silently clears it), this keeps exactly what the user typed/pasted visible,
 * shows a red border + inline error when it is not a valid number, and only
 * commits the cleaned value to the form once valid — so the user can see and
 * fix the bad value instead of losing it without explanation.
 *
 * Typing and pasting flow through the same `onChange`, so both are validated.
 */
const NumericInput: React.FC<NumericInputProps> = ({
  value,
  onChange,
  thousandSeparator = true,
  status,
  onFocus,
  onBlur,
  ...rest
}) => {
  const [text, setText] = useState<string>(() =>
    toDisplay(value, thousandSeparator),
  );
  const [error, setError] = useState<string | null>(null);
  const focused = useRef(false);

  // Sync the display with external value changes (form reset / setFieldsValue),
  // but never fight the user while they are editing.
  useEffect(() => {
    if (!focused.current) {
      setText(toDisplay(value, thousandSeparator));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, thousandSeparator]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setText(raw);

    if (raw.trim() === '') {
      setError(null);
      onChange?.(null);
      return;
    }

    const cleaned = stripFormatting(raw);
    if (!PARTIAL_NUMBER.test(cleaned)) {
      // genuinely malformed ("12.34.56", "abc") — keep it visible, don't commit
      setError(invalidMessage(raw));
      onChange?.(null);
      return;
    }

    // valid so far: commit once it is a complete number, otherwise stay quiet
    // (mid-typing "-" / "10." / "1e") without committing or erroring.
    setError(null);
    onChange?.(parseFormattedNumber(raw) !== null ? cleaned : null);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    focused.current = true;
    // strip thousand separators so the value is easy to edit
    if (thousandSeparator && parseFormattedNumber(text) !== null) {
      setText(stripFormatting(text));
    }
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    focused.current = false;
    const raw = text;
    if (raw.trim() === '') {
      setError(null);
      onChange?.(null);
    } else if (parseFormattedNumber(raw) === null) {
      // left incomplete/invalid ("-", "12.34.56") — flag it
      setError(invalidMessage(raw));
    } else {
      setError(null);
      const cleaned = stripFormatting(raw);
      onChange?.(cleaned);
      setText(toDisplay(cleaned, thousandSeparator));
    }
    onBlur?.(e);
  };

  return (
    <>
      <StyledNumericInput
        {...rest}
        inputMode="decimal"
        value={text}
        status={error ? 'error' : status}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      {error && (
        <div
          className="ant-form-item-explain ant-form-item-explain-connected"
          role="alert"
        >
          <div className="ant-form-item-explain-error">{error}</div>
        </div>
      )}
    </>
  );
};

export default NumericInput;
